/**
 * Phase 8 T-06c §21.D — Smart Match preferences (localStorage only).
 *
 * Persisted under `ggj.smartmatch.preferences`. Three inner tabs:
 *   - `general`         → score threshold, geography, seniority
 *   - `visibility`      → private / team / org
 *   - `shared-context`  → company / team description, must-haves
 *
 * Last-opened tab is remembered under `ggj.smartmatch.preferences.lastTab`
 * per §21.D acceptance.
 */

export type PreferencesTab = 'general' | 'visibility' | 'shared-context';

export type VisibilityScope = 'private' | 'team' | 'org';

export interface SmartMatchPreferences {
  general: {
    scoreThreshold: number;
    geography: string;
    seniorityMin: string;
    seniorityMax: string;
  };
  visibility: {
    scope: VisibilityScope;
  };
  sharedContext: {
    companyDescription: string;
    teamDescription: string;
    mustHaves: string;
  };
}

export const DEFAULT_PREFERENCES: SmartMatchPreferences = {
  general: {
    scoreThreshold: 70,
    geography: 'Global',
    seniorityMin: 'Junior',
    seniorityMax: 'Principal',
  },
  visibility: {
    scope: 'team',
  },
  sharedContext: {
    companyDescription: '',
    teamDescription: '',
    mustHaves: '',
  },
};

const LS_PREFS = 'ggj.smartmatch.preferences';
const LS_PREFS_V1 = 'ggj.smartMatch.preferences.v1';
const LS_LAST_TAB = 'ggj.smartmatch.preferences.lastTab';

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readPreferences(): SmartMatchPreferences {
  const s = safeStorage();
  if (!s) return DEFAULT_PREFERENCES;
  try {
    const raw = s.getItem(LS_PREFS);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<SmartMatchPreferences>;
    return {
      general: { ...DEFAULT_PREFERENCES.general, ...(parsed.general ?? {}) },
      visibility: {
        scope:
          parsed.visibility?.scope === 'private' ||
          parsed.visibility?.scope === 'org' ||
          parsed.visibility?.scope === 'team'
            ? parsed.visibility.scope
            : 'team',
      },
      sharedContext: {
        ...DEFAULT_PREFERENCES.sharedContext,
        ...(parsed.sharedContext ?? {}),
      },
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function writePreferences(prefs: SmartMatchPreferences): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(LS_PREFS, JSON.stringify(prefs));
  } catch {
    /* quota / private mode */
  }
}

/** Canonical Phase-8 modal state key (Addendum F C-08). */
export function readPreferencesV1(): SmartMatchPreferences | null {
  const s = safeStorage();
  if (!s) return null;
  try {
    const raw = s.getItem(LS_PREFS_V1);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SmartMatchPreferences>;
    return {
      general: { ...DEFAULT_PREFERENCES.general, ...(parsed.general ?? {}) },
      visibility: {
        scope:
          parsed.visibility?.scope === 'private' ||
          parsed.visibility?.scope === 'org' ||
          parsed.visibility?.scope === 'team'
            ? parsed.visibility.scope
            : 'team',
      },
      sharedContext: {
        ...DEFAULT_PREFERENCES.sharedContext,
        ...(parsed.sharedContext ?? {}),
      },
    };
  } catch {
    return null;
  }
}

export function writePreferencesV1(prefs: SmartMatchPreferences): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(LS_PREFS_V1, JSON.stringify(prefs));
  } catch {
    /* quota / private mode */
  }
}

export function readLastPreferencesTab(): PreferencesTab {
  const s = safeStorage();
  if (!s) return 'general';
  try {
    const raw = s.getItem(LS_LAST_TAB);
    if (raw === 'general' || raw === 'visibility' || raw === 'shared-context') {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return 'general';
}

export function writeLastPreferencesTab(tab: PreferencesTab): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(LS_LAST_TAB, tab);
  } catch {
    /* ignore */
  }
}
